import { Link } from "react-router-dom";
import {
  ArrowUpRight, Award, ShieldCheck, MapPin,
  LayoutGrid, Layers, Trees, Droplets, Wrench, Flame, Columns, Sparkles
} from "lucide-react";
import { FadeUp, Counter } from "../components/Motion";
import PageMeta from "../components/PageMeta";

const HERO_IMG  = "https://images.unsplash.com/photo-1738193830098-2d92352a1856?crop=entropy&cs=srgb&fm=jpg&q=85&w=2200";
const STONE_IMG = "https://images.unsplash.com/photo-1779565145536-ccdf57517151?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const PLANT_IMG = "https://images.unsplash.com/photo-1749803915455-a7642520d0d3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const DECK_IMG  = "https://customer-assets.emergentagent.com/job_yard-design-studio-2/artifacts/00qfjro5_Nano%20Banana%202%20-%20Premium%20composite%20and%20hardwood%20decks%20engineered%20for%20longevity_%20Custom-designed%20pergo.png";
const FIRE_IMG  = "https://images.unsplash.com/photo-1779148478270-da34ded6cd4b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const SERVICE_GROUPS = [
  {
    title: "Design",
    items: [
      { icon: LayoutGrid, name: "Full Landscape Design" },
      { icon: Sparkles,   name: "Redesign & Refresh" },
      { icon: Trees,      name: "Flower Beds & Planting Plans" },
    ],
  },
  {
    title: "Hardscape",
    items: [
      { icon: LayoutGrid, name: "Paver Patios" },
      { icon: Layers,     name: "Retaining Walls" },
      { icon: Wrench,     name: "Grading & Top Soil" },
    ],
  },
  {
    title: "Outdoor Structures",
    items: [
      { icon: Columns,    name: "Pergolas" },
      { icon: Wrench,     name: "Decks & Composite Decks" },
      { icon: Wrench,     name: "Fences" },
      { icon: Flame,      name: "Fire Pits & Features" },
    ],
  },
  {
    title: "Green & Soft Landscape",
    items: [
      { icon: Trees,      name: "Sod, Re-Sodding & Turf" },
      { icon: Trees,      name: "Trees & Shrubs" },
      { icon: Sparkles,   name: "Artificial Turf" },
      { icon: Wrench,     name: "Mulch & Decorative Rock" },
    ],
  },
  {
    title: "Systems",
    items: [
      { icon: Droplets,   name: "Smart Irrigation" },
      { icon: Sparkles,   name: "Landscape Lighting" },
      { icon: Wrench,     name: "Yard Maintenance" },
    ],
  },
];

const AREAS = ["Edmonton", "St. Albert", "Sherwood Park", "Beaumont", "Leduc", "Spruce Grove", "Fort Saskatchewan", "Stony Plain"];

// Reusable inline CTA
const PrimaryCTA = ({ children = "Get Free Estimate", to = "/estimate" }) => (
  <Link
    to={to}
    className="inline-flex items-center gap-2 px-7 py-4 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm tracking-widest uppercase transition-colors duration-300"
  >
    {children} <ArrowUpRight className="w-4 h-4" />
  </Link>
);

const GhostCTA = ({ children, to }) => (
  <Link
    to={to}
    className="inline-flex items-center gap-2 px-7 py-4 border border-[#F5F3EF]/40 hover:bg-[#F5F3EF] hover:text-[#151515] text-[#F5F3EF] text-sm tracking-widest uppercase transition-all duration-300"
  >
    {children} <ArrowUpRight className="w-4 h-4" />
  </Link>
);

// Alternating image+text section
function Section({ id, eyebrow, title, body, image, reverse = false, cta }) {
  return (
    <section id={id} className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-[#D8D6D0]/10">
      <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}>
        <FadeUp className="lg:col-span-7">
          <div className="aspect-[4/3] overflow-hidden">
            <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-1000 hover:scale-105" />
          </div>
        </FadeUp>
        <FadeUp delay={0.1} className="lg:col-span-5">
          <div className="overline text-[#8C1D30] mb-4">{eyebrow}</div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-none mb-6">{title}</h2>
          <div className="text-[#D8D6D0]/75 leading-relaxed space-y-4">{body}</div>
          {cta && <div className="mt-8">{cta}</div>}
        </FadeUp>
      </div>
    </section>
  );
}

export default function Residential() {
  return (
    <div data-testid="residential-page">
      <PageMeta
        title="Residential Landscaping Edmonton — Design, Hardscape & Outdoor Living"
        description="Full residential landscape design, paver patios, retaining walls, fences, planting, lighting, and outdoor living builds across Edmonton, St. Albert, Sherwood Park and the region."
        path="/residential"
        image="https://images.unsplash.com/photo-1738193830098-2d92352a1856?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
      />
      {/* ---------- HERO ---------- */}
      <section className="relative h-[85vh] min-h-[600px] w-full overflow-hidden">
        <img src={HERO_IMG} alt="Residential landscaping" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative h-full flex flex-col justify-end pb-20 px-6 md:px-12 lg:px-16">
          <FadeUp>
            <div className="overline text-[#D8D6D0]/80 mb-6">Residential Landscaping</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] max-w-5xl">
              Big project or small — <span className="italic text-[#D8D6D0]">we're here to design it.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#D8D6D0]/80 max-w-2xl leading-relaxed">
              Full landscape design, hardscaping, planting, structures, irrigation and maintenance for homeowners across Edmonton and surrounding areas.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <PrimaryCTA />
              <GhostCTA to="/yard-designer">Design My Yard with AI</GhostCTA>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ---------- INTRO / TRUST ---------- */}
      <section className="px-6 md:px-12 lg:px-16 py-24 border-y border-[#D8D6D0]/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <FadeUp className="lg:col-span-7">
            <div className="overline text-[#8C1D30] mb-4">Approach</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-none mb-8">
              Landscapes that feel like <span className="italic text-[#D8D6D0]">an extension of the home.</span>
            </h2>
            <p className="text-[#D8D6D0]/75 leading-relaxed text-lg">
              At NorthCrest, we treat residential landscaping with the same architectural rigour as the homes they surround. We compose hardscape rhythm, planting palettes and lighting choreography as a single editorial vision — built to last Alberta winters and grow more beautiful every season.
            </p>
            <p className="text-[#D8D6D0]/65 leading-relaxed mt-4">
              From a single retaining wall to a complete outdoor living transformation, every project begins with understanding how you live and ends with a yard composed for both function and feeling.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <PrimaryCTA />
              <Link to="/projects" className="link-underline text-sm uppercase tracking-widest text-[#D8D6D0]/80 self-center">View Projects →</Link>
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-5 space-y-8">
            <div>
              <Award className="w-6 h-6 text-[#8C1D30] mb-3" strokeWidth={1.25} />
              <div className="font-serif-display text-4xl"><Counter value={200} suffix="+" /></div>
              <div className="overline text-[#D8D6D0]/50 mt-2">Residential Projects</div>
            </div>
            <div>
              <MapPin className="w-6 h-6 text-[#8C1D30] mb-3" strokeWidth={1.25} />
              <div className="font-serif-display text-4xl"><Counter value={8} /></div>
              <div className="overline text-[#D8D6D0]/50 mt-2">Communities Served</div>
            </div>
            <div>
              <ShieldCheck className="w-6 h-6 text-[#8C1D30] mb-3" strokeWidth={1.25} />
              <div className="font-serif-display text-4xl">100%</div>
              <div className="overline text-[#D8D6D0]/50 mt-2">Licensed & Insured</div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ---------- SERVICES GROUPED ---------- */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <FadeUp>
          <div className="max-w-3xl mb-16">
            <div className="overline text-[#8C1D30] mb-4">Services</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-none">
              Every element of <span className="italic text-[#D8D6D0]">outdoor living.</span>
            </h2>
          </div>
        </FadeUp>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D8D6D0]/10">
          {SERVICE_GROUPS.map((group, gi) => (
            <FadeUp key={group.title} delay={gi * 0.05}>
              <div className="bg-[#151515] p-8 lg:p-10 h-full">
                <div className="overline text-[#D8D6D0]/40 mb-6">0{gi + 1}</div>
                <div className="font-serif-display text-2xl md:text-3xl mb-8">{group.title}</div>
                <ul className="space-y-4">
                  {group.items.map((it, i) => {
                    const Ico = it.icon;
                    return (
                      <li key={it.name} className="flex items-center gap-3 text-sm text-[#D8D6D0]/80 hover:text-[#F5F3EF] transition-colors">
                        <Ico className="w-4 h-4 text-[#8C1D30]" strokeWidth={1.25} />
                        {it.name}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </FadeUp>
          ))}
          <FadeUp delay={0.3}>
            <Link to="/estimate" className="group bg-[#0B2B1F] p-8 lg:p-10 h-full flex flex-col justify-between hover:bg-[#8C1D30] transition-colors duration-500">
              <div>
                <div className="overline text-[#F5F3EF]/60 mb-6">06</div>
                <div className="font-serif-display text-2xl md:text-3xl">Start Your Project.</div>
                <p className="text-sm text-[#D8D6D0]/70 mt-4">Free consultation. No deposit. No obligation.</p>
              </div>
              <div className="mt-8 flex items-center gap-2 text-xs uppercase tracking-widest text-[#F5F3EF]">
                Get Free Estimate <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* ---------- EDITORIAL DEEP-DIVE SECTIONS ---------- */}
      <Section
        id="hardscape"
        eyebrow="Paving & Retaining"
        title="Paving Stones & Retaining Walls."
        image={STONE_IMG}
        body={
          <>
            <p>
              Hardscape is the architecture of your yard. Precision-laid European pavers, engineered retaining wall systems, natural stone borders, and proper drainage — built on the engineered base specifications every NorthCrest project requires.
            </p>
            <p>
              From walkways and patio stones to multi-tier retaining walls and fire pit lounges, our hardscape is built to withstand Edmonton's freeze-thaw cycles and look better in year fifteen than year one.
            </p>
          </>
        }
        cta={<PrimaryCTA>Free Estimate</PrimaryCTA>}
      />

      <Section
        id="plants"
        eyebrow="Trees, Shrubs, Mulch & Rock"
        title="Planting palettes composed for Alberta."
        image={PLANT_IMG}
        reverse
        body={
          <>
            <p>
              The right selection of trees, shrubs and groundcover transforms the feel of any yard — boosting curb appeal, providing shade and privacy, and inviting nature back into your space.
            </p>
            <p>
              We carefully choose plants proven to thrive in Alberta's climate, paired with decorative rock and premium mulches that finish the composition. Designed to look intentional from week one and only better with time.
            </p>
          </>
        }
        cta={<PrimaryCTA />}
      />

      <Section
        id="structures"
        eyebrow="Decks · Fences · Pergolas"
        title="Outdoor structures, architecturally detailed."
        image={DECK_IMG}
        body={
          <>
            <p>
              Premium composite and hardwood decks engineered for longevity. Custom-designed pergolas — louvered, slatted, or motorized. Privacy fencing built with pressure-treated lumber that matches the architectural language of your home.
            </p>
            <p>
              Hidden fastener systems, integrated lighting, and architectural railing details — every structure executed to portfolio-grade finish.
            </p>
          </>
        }
        cta={<PrimaryCTA />}
      />

      <Section
        id="fire-irrigation"
        eyebrow="Fire & Water"
        title="Fire features, water features, irrigation."
        image={FIRE_IMG}
        reverse
        body={
          <>
            <p>
              Sculptural fire pits and gas features as the architectural centrepiece of your outdoor lounge. Subtle water elements that bring soundscape to the garden. Smart, water-efficient irrigation systems controlled from your phone.
            </p>
            <p>
              The systems that make a beautiful yard easy to live in.
            </p>
          </>
        }
        cta={<PrimaryCTA />}
      />

      {/* ---------- MAINTENANCE ---------- */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-t border-[#D8D6D0]/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-7">
            <div className="overline text-[#8C1D30] mb-4">Yard Maintenance</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-none mb-6">
              Keep your yard <span className="italic text-[#D8D6D0]">composed.</span>
            </h2>
            <p className="text-[#D8D6D0]/75 leading-relaxed">
              Seasonal maintenance plans tailored to your specific landscape — grass cutting, weeding, plant trimming, fertilization, irrigation start-up and winterization. Let us take care of upkeep so you can enjoy the yard.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-5 flex items-end">
            <div className="w-full nc-card p-8">
              <div className="overline text-[#D8D6D0]/50 mb-3">Maintenance Plans</div>
              <ul className="space-y-3 text-sm text-[#D8D6D0]/80">
                <li className="flex items-center gap-3"><span className="w-1 h-1 bg-[#8C1D30]" /> Weekly mowing & edging</li>
                <li className="flex items-center gap-3"><span className="w-1 h-1 bg-[#8C1D30]" /> Spring & fall clean-ups</li>
                <li className="flex items-center gap-3"><span className="w-1 h-1 bg-[#8C1D30]" /> Pruning & plant care</li>
                <li className="flex items-center gap-3"><span className="w-1 h-1 bg-[#8C1D30]" /> Irrigation activation & blow-out</li>
                <li className="flex items-center gap-3"><span className="w-1 h-1 bg-[#8C1D30]" /> Fertilization & aeration</li>
              </ul>
              <div className="mt-6">
                <PrimaryCTA>Get a Plan</PrimaryCTA>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ---------- SERVICE AREAS ---------- */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 bg-[#0B2B1F]">
        <FadeUp>
          <div className="overline text-[#D8D6D0]/70 mb-4">Service Areas</div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-none mb-12 max-w-3xl">
            Locally owned & operated. <span className="italic">Edmonton and surrounding.</span>
          </h2>
        </FadeUp>
        <div className="flex flex-wrap gap-3">
          {AREAS.map((a, i) => (
            <FadeUp key={a} delay={i * 0.03}>
              <Link to={`/areas/${a.toLowerCase().replace(/\.\s*/g, "").replace(/\s+/g, "-")}`} className="px-5 py-3 border border-[#F5F3EF]/30 text-sm uppercase tracking-widest text-[#F5F3EF] hover:bg-[#F5F3EF] hover:text-[#0B2B1F] transition-colors">
                {a}
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ---------- COMMERCIAL CROSS-LINK ---------- */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 border-y border-[#D8D6D0]/10">
        <FadeUp>
          <div className="overline text-[#8C1D30] mb-4">Also</div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-none mb-6 max-w-3xl">
            We also deliver <Link to="/services/commercial" className="italic text-[#D8D6D0] link-underline">commercial landscaping.</Link>
          </h2>
          <p className="text-[#D8D6D0]/65 leading-relaxed max-w-2xl">
            Developers, retail, office, multi-family — full-service commercial landscape design, construction, snow removal, and maintenance across Alberta.
          </p>
          <div className="mt-8">
            <GhostCTA to="/services/commercial">Commercial Services</GhostCTA>
          </div>
        </FadeUp>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <FadeUp>
          <div className="max-w-4xl">
            <div className="overline text-[#8C1D30] mb-4">Begin</div>
            <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95]">
              Project big or small. <span className="italic text-[#D8D6D0]">Let's start a conversation.</span>
            </h2>
            <p className="text-[#D8D6D0]/65 mt-8 max-w-2xl text-lg">
              A senior designer will reach out within 24 business hours to schedule a complimentary on-site consultation.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <PrimaryCTA />
              <a href="tel:+17806557604" className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-[#F5F3EF]/40 hover:bg-[#F5F3EF] hover:text-[#151515] text-[#F5F3EF] text-sm tracking-widest uppercase transition-all duration-300">
                (780) 655-7604
              </a>
            </div>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
